import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import modalStyle from "../../styles/modalStyle";
import { modalTypes } from "../manageSegment/manageSegment";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import getUserColDef from "../../colDef/usersColumnDef";
import DropDownValue from "../baseComponents/dropdownvalue";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import User from "../../interfaces/user";

interface HandleSegmentUsersModalProps {
    modalVisible: boolean;
    closeModal: () => void;
    modalType: string;
    selectedSegmentId: string;
};

const HandleSegmentUsersModal: React.FC<HandleSegmentUsersModalProps> = ({ modalVisible, closeModal, modalType, selectedSegmentId }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [segmentUsers, setSegmentUsers] = useState<User[]>([]);
    const [newSegmentUsers, setNewSegmentUsers] = useState<string[]>([]);
    const users: User[] = useSelector((state: RootState) => state.userList.users);
    const currentUserList = modalType === modalTypes.USER_ADD ? users : segmentUsers;
    const finalColumnDef = getUserColDef();

    useEffect(() => {
        fetchSegmentUsers();
    }, [selectedSegmentId])

    const handleCloseModal = () => {
        setNewSegmentUsers([]);
        closeModal();
    };

    const fetchSegmentUsers = async () => {
        try {
            const response = await apiClient.get(`/api/v1/user?segment_id=${selectedSegmentId}`);
            if (response.status === 200) {
                setSegmentUsers(response.data.user_responses as User[]);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        try {
            const body = {
                username: newSegmentUsers,
                segment_id: [selectedSegmentId]
            };
            let response = null;
            if (modalType === modalTypes.USER_ADD) {
                response = await apiClient.post("/api/v1/user_segment", body);
            } else {
                response = await apiClient.delete("/api/v1/user_segment", {
                    data: body
                });
            }
            if (response.status === 200) {
                if (modalType === modalTypes.USER_ADD) {
                    alert('Success! Added users to segment');
                } else {
                    alert('Success! Removed users from segment');
                }
                handleCloseModal();
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    return (
        <Modal open={modalVisible} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                {loading ? (
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                        <CircularProgress size={50} />
                    </Box>
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography id="modal-title" variant="h6">
                                {modalType === modalTypes.USER_ADD ? 'Add New' : 'Remove'} Segment User
                            </Typography>
                            <IconButton onClick={handleCloseModal}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <DropDownValue
                            label="Select Users"
                            items={currentUserList}
                            selectedValue={newSegmentUsers}
                            onSelection={val => setNewSegmentUsers(val)}
                            required
                            multiple
                            keyProp="username"
                            valueProp="username"
                            displayProp="username"
                            style={{ mt: 1, mb: 2 }}
                        />
                        <Box sx={{ mt: 1}} textAlign="right">
                            <Button variant="contained" color="primary" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Box>
                        <Typography sx={{ mb: 1 }} variant="h6">
                            Current Users in Segment
                        </Typography>
                        <SearchResultGrid
                            height='50vh'
                            getRowId={(row) => row.username}
                            results={segmentUsers}
                            onSelectRow={() => {}}
                            columns={finalColumnDef}
                            rowCount={segmentUsers.length}
                            isRowSelectionAllowed={false}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
};

export default HandleSegmentUsersModal;